// tippi/src/pages/admin/invoices/AdminInvoices.js
import React, { useMemo, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../firebase/config';
import { useAllUsers, useAllOrganizations } from '../../../hooks/useAdminData';
import { useInvoiceCustomers } from '../../../hooks/useInvoiceCustomers';
import { useInvoiceFeature } from '../../../hooks/useInvoiceFeature';
import { COLORS } from '../../../styles/tokens';
import AdminInvoiceList from './AdminInvoiceList';
import AdminFeeInvoices from './AdminFeeInvoices';

const approveFn = httpsCallable(getFunctions(undefined, 'europe-west1'), 'approveInvoiceCustomer');

const kr = n => `kr ${Number(n ?? 0).toLocaleString('nb-NO')}`;
const TABS = [
  { key: 'customers', label: 'Fakturakunder' },
  { key: 'invoices', label: 'Fakturaer' },
  { key: 'fees', label: 'Gebyrfakturaer' },
];

export default function AdminInvoices() {
  const [tab, setTab] = useState('customers');
  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Faktura</h1>
      <div role="tablist" style={styles.tabs}>
        {TABS.map(t => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
                  style={{ ...styles.tab, ...(tab === t.key ? styles.tabOn : {}) }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'customers' && <CustomersTab />}
      {tab === 'invoices' && <AdminInvoiceList />}
      {tab === 'fees' && <AdminFeeInvoices />}
    </div>
  );
}

function CustomersTab() {
  const { users } = useAllUsers();
  const { organizations, error: orgsError } = useAllOrganizations();
  const { customers, error: customersError } = useInvoiceCustomers();
  const feature = useInvoiceFeature();
  const [query, setQuery] = useState('');
  const [openKey, setOpenKey] = useState(null);

  const rows = useMemo(() => {
    const list = [
      ...users.filter(u => (u.roles ?? []).includes('orderer')).map(u => ({
        key: `user_${u.id}`, type: 'user', id: u.id, name: u.name ?? u.email ?? u.id, detail: u.email ?? '',
      })),
      ...organizations.map(o => ({
        key: `org_${o.id}`, type: 'org', id: o.id, name: o.orgName ?? o.id, detail: o.orgNumber ? `Org.nr. ${o.orgNumber}` : '',
      })),
    ];
    const q = query.trim().toLowerCase();
    return list
      .filter(r => !q || `${r.name} ${r.detail}`.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, 'nb'));
  }, [users, organizations, query]);

  async function toggleFeature() {
    // Skrur man faktura AV, tvinges kort PÅ i samme skriv, så plattformen aldri står uten betalingsmåte.
    const next = !feature.enabled;
    await setDoc(doc(db, 'settings', 'platform'),
      next ? { invoicePaymentEnabled: true } : { invoicePaymentEnabled: false, cardPaymentEnabled: true },
      { merge: true });
  }

  return (
    <>
      <div style={styles.flagCard}>
        <div>
          <div style={styles.flagTitle}>Fakturabetaling</div>
          <div style={styles.hint}>
            {feature.enabled ? 'På: godkjente kunder kan velge faktura.' : 'Av: ingen kan velge faktura ennå.'}
          </div>
        </div>
        <button type="button" role="switch" aria-checked={feature.enabled} aria-label="Fakturabetaling"
                onClick={toggleFeature}
                style={{ ...styles.track, background: feature.enabled ? COLORS.success : COLORS.border }}>
          <span style={{ ...styles.knob, transform: feature.enabled ? 'translateX(20px)' : 'none' }} />
        </button>
      </div>

      {(orgsError || customersError) && <p role="alert" style={styles.error}>Kunne ikke hente kundedata. Last siden på nytt.</p>}

      <input aria-label="Søk etter kunde" placeholder="Søk etter navn, e-post eller org.nr." value={query}
             onChange={e => setQuery(e.target.value)} style={styles.search} />

      {rows.map(r => (
        <CustomerRow key={r.key} row={r} customer={customers[r.key]}
                     open={openKey === r.key} onToggle={() => setOpenKey(openKey === r.key ? null : r.key)}
                     onDone={() => setOpenKey(null)} />
      ))}
      {rows.length === 0 && <p style={styles.hint}>Ingen kunder funnet.</p>}
    </>
  );
}

function CustomerRow({ row, customer, open, onToggle, onDone }) {
  const approved = customer?.approved === true;
  return (
    <div data-testid="customer-row" style={styles.row}>
      <div style={styles.rowTop}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={styles.name}>{row.name}</div>
          <div style={styles.hint}>{row.type === 'org' ? 'Organisasjon' : 'Bestiller'}{row.detail ? ` · ${row.detail}` : ''}</div>
          {customer && (customer.termsDays || customer.creditLimit != null) && (
            <div style={styles.hint}>
              {customer.termsDays} dager · grense {kr(customer.creditLimit)} · utestående {kr(customer.exposure)}
            </div>
          )}
        </div>
        <span style={{ ...styles.chip, ...(approved ? styles.chipOk : styles.chipOff) }}>{approved ? 'Godkjent' : 'Ikke godkjent'}</span>
        <button type="button" style={styles.smallBtn} onClick={onToggle}>{approved ? 'Endre' : 'Godkjenn'}</button>
      </div>
      {open && <ApprovalForm row={row} customer={customer} approved={approved} onDone={onDone} />}
    </div>
  );
}

function ApprovalForm({ row, customer, approved, onDone }) {
  const [termsDays, setTermsDays] = useState(String(customer?.termsDays ?? 30));
  const [creditLimit, setCreditLimit] = useState(customer?.creditLimit != null ? String(customer.creditLimit) : '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(approve) {
    setError('');
    if (!reason.trim()) { setError('Skriv en begrunnelse'); return; }
    const limit = Number(creditLimit);
    if (approve && (!Number.isInteger(limit) || limit <= 0)) { setError('Kredittgrensen må være et helt beløp over 0'); return; }
    setBusy(true);
    try {
      await approveFn({
        targetType: row.type, targetId: row.id, approved: approve,
        ...(approve ? { termsDays: Number(termsDays), creditLimit: limit } : {}),
        reason: reason.trim(),
      });
      onDone();
    } catch (err) {
      setError(err?.message || 'Kunne ikke lagre. Prøv igjen.');
      setBusy(false);
    }
  }

  return (
    <div style={styles.form}>
      <label style={styles.label} htmlFor={`terms-${row.key}`}>Betalingsfrist</label>
      <select id={`terms-${row.key}`} value={termsDays} onChange={e => setTermsDays(e.target.value)} style={styles.input}>
        <option value="14">14 dager</option>
        <option value="30">30 dager</option>
      </select>
      <label style={styles.label} htmlFor={`limit-${row.key}`}>Kredittgrense (kr)</label>
      <input id={`limit-${row.key}`} inputMode="numeric" value={creditLimit}
             onChange={e => setCreditLimit(e.target.value.replace(/\D/g, ''))} style={styles.input} />
      <label style={styles.label} htmlFor={`reason-${row.key}`}>Begrunnelse</label>
      <textarea id={`reason-${row.key}`} value={reason} onChange={e => setReason(e.target.value)} rows={2}
                maxLength={500} style={{ ...styles.input, height: 64, padding: 12 }} />
      {error && <p role="alert" style={styles.error}>{error}</p>}
      <div style={styles.actions}>
        <button type="button" style={styles.primary} disabled={busy} onClick={() => submit(true)}>Lagre godkjenning</button>
        {approved && <button type="button" style={styles.danger} disabled={busy} onClick={() => submit(false)}>Trekk tilbake godkjenning</button>}
      </div>
    </div>
  );
}

const styles = {
  page:     { maxWidth: 860 },
  title:    { fontSize: 24, fontWeight: 700, color: COLORS.textDark, margin: '0 0 12px' },
  tabs:     { display: 'flex', gap: 8, borderBottom: `1px solid ${COLORS.border}`, marginBottom: 16 },
  tab:      { background: 'none', border: 'none', borderBottomWidth: 3, borderBottomStyle: 'solid', borderBottomColor: 'transparent', padding: '10px 6px', fontSize: 15, fontWeight: 600, color: COLORS.textLight, cursor: 'pointer' },
  tabOn:    { color: COLORS.textDark, borderBottomColor: COLORS.accent },
  flagCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 14 },
  flagTitle:{ fontWeight: 700, fontSize: 15, color: COLORS.textDark },
  hint:     { fontSize: 12.5, color: COLORS.textMid, marginTop: 2 },
  track:    { position: 'relative', width: 50, height: 30, borderRadius: 15, border: 'none', cursor: 'pointer', flexShrink: 0, padding: 0 },
  knob:     { position: 'absolute', top: 3, left: 3, width: 24, height: 24, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'transform 0.15s' },
  search:   { width: '100%', boxSizing: 'border-box', height: 44, padding: '0 14px', borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 14.5, marginBottom: 12, background: COLORS.white, color: COLORS.textDark },
  row:      { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8 },
  rowTop:   { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  name:     { fontWeight: 700, fontSize: 15, color: COLORS.textDark },
  chip:     { borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },
  chipOk:   { background: COLORS.successBg, color: COLORS.successText },
  chipOff:  { background: COLORS.surfaceHover, color: COLORS.textLight },
  smallBtn: { border: `1.5px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, borderRadius: 18, padding: '6px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  form:     { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column' },
  label:    { fontSize: 12, fontWeight: 600, color: COLORS.textMid, margin: '8px 0 4px' },
  input:    { boxSizing: 'border-box', height: 42, padding: '0 12px', borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 14.5, fontFamily: 'inherit', background: COLORS.white, color: COLORS.textDark },
  error:    { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '8px 12px', borderRadius: 8, fontSize: 13.5, margin: '10px 0 0' },
  actions:  { display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' },
  primary:  { background: COLORS.accent, color: '#1a1a2e', border: 'none', borderRadius: 20, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  danger:   { background: COLORS.white, color: COLORS.danger, border: `1.5px solid ${COLORS.danger}`, borderRadius: 20, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
};
